import KuikaSkeleton, { KuikaSkeletonRegion } from "../../../ui-kit/kuika-skeleton/kuika-skeleton";

/**
 * Placeholder for the right panel while the project loads.
 *
 * It draws the column the panel opens on — a screen root is what is selected once a project
 * loads — block by block, from the real components' geometry: two grouped property cards (44px
 * rows, a 24px tile, dividers from the label's x), a divider, the Actions header and two action
 * cards (a 12px title over a 10px return-type line), another divider, and the
 * Screen Inputs header with one card. Insets are the column's own 16px / 8px.
 *
 * The rows deliberately stop at labels and headers instead of miming what is inside them: an open
 * section's contents vary per component (a switch, a colour picker and a bound text field are all
 * different heights), so drawing them would promise a layout that rarely arrives. A trigger label
 * is drawn as a bar for the same reason — its event name depends on the selection.
 */

interface ISkeletonRow {
  labelWidth: string;
  /** A select row's value on the right; absent on switch and section rows. */
  valueWidth?: string;
}

/** Group one mirrors the screen's Role / Step Screen / Is Offline, group two its sections. */
const GROUPS: ISkeletonRow[][] = [
  [{ labelWidth: "22%", valueWidth: "18%" }, { labelWidth: "52%" }, { labelWidth: "30%" }],
  [{ labelWidth: "40%" }, { labelWidth: "34%" }, { labelWidth: "28%" }]
];

/** Varied so the cards read as a list of actions, not a printed pattern. */
const ACTION_TITLE_WIDTHS: string[] = ["46%", "58%"];

interface ISkeletonSectionHeaderProps {
  titleWidth: number;
}

interface ISkeletonCardProps {
  titleWidth: string;
}

const SkeletonDivider = (): JSX.Element => <div className="kuika-properties__skeleton-divider" />;

const SkeletonSectionHeader = ({ titleWidth }: ISkeletonSectionHeaderProps): JSX.Element => (
  <div className="kuika-properties__skeleton-header">
    <KuikaSkeleton width={titleWidth} height={14} />
    <KuikaSkeleton className="kuika-properties__skeleton-add" width={24} height={24} />
  </div>
);

const SkeletonCard = ({ titleWidth }: ISkeletonCardProps): JSX.Element => (
  <div className="kuika-properties__skeleton-card">
    <div className="kuika-properties__skeleton-text">
      <KuikaSkeleton width={titleWidth} height={12} />
      <KuikaSkeleton width="34%" height={10} />
    </div>
  </div>
);

// The shapes stay hidden from assistive tech; the region around them is busy and says what it
// loads, as every other skeleton's owner does.
const PropertiesSkeleton = (): JSX.Element => (
  <KuikaSkeletonRegion label="Loading properties">
    <div className="kuika-properties__skeleton" aria-hidden="true" data-testid="propertiesSkeleton">
      {GROUPS.map((rows: ISkeletonRow[], groupIndex: number) => (
        <div key={groupIndex} className="kuika-properties__skeleton-group">
          {rows.map((row: ISkeletonRow, rowIndex: number) => (
            <div key={rowIndex} className="kuika-properties__skeleton-row">
              <KuikaSkeleton className="kuika-properties__skeleton-tile" width={24} height={24} />
              <KuikaSkeleton width={row.labelWidth} height={12} />
              {row.valueWidth && (
                <KuikaSkeleton className="kuika-properties__skeleton-value" width={row.valueWidth} height={10} />
              )}
            </div>
          ))}
        </div>
      ))}
      <SkeletonDivider />
      <SkeletonSectionHeader titleWidth={56} />
      <KuikaSkeleton className="kuika-properties__skeleton-trigger" width={64} height={10} />
      {ACTION_TITLE_WIDTHS.map((width: string, index: number) => (
        <SkeletonCard key={index} titleWidth={width} />
      ))}
      <SkeletonDivider />
      <SkeletonSectionHeader titleWidth={96} />
      <SkeletonCard titleWidth="40%" />
    </div>
  </KuikaSkeletonRegion>
);

export default PropertiesSkeleton;
